import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice } from '../utils/currency';
import SEO from '../components/SEO';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, updateCartItem, removeFromCart, loading } = useCart();
  const { t, language } = useLanguage();

  // Calculate proper cart total considering tuning products with Stage 1 price
  const calculateCartTotal = () => {
    if (!cart.items || cart.items.length === 0) return 0;
    
    return cart.items.reduce((total, item) => {
      const itemPrice = item.product.category === 'handheld_tuners' 
        ? 300 // 1300 PLN = 300 EUR (Stage 1 base price)
        : item.product.price;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const cartTotal = calculateCartTotal();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <>
        <SEO
          title="Shopping Cart - Empty"
          description="Your shopping cart is empty. Browse our selection of high-performance motorcycle parts and accessories."
          keywords="shopping cart, checkout, motorcycle parts"
          noindex={true}
        />
        <div className="container-custom py-12 text-center">
          <h1 className="text-3xl font-bold mb-4">{t('shoppingCart.emptyCart')}</h1>
          <p className="text-gray-600 mb-8">{t('shoppingCart.startShopping')}</p>
          <Link to="/shop" className="btn btn-primary">
            {t('shoppingCart.browseProducts')}
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`Shopping Cart (${cart.item_count} items)`}
        description="Review your motorcycle parts order and proceed to checkout. Secure payment and fast shipping available."
        keywords="shopping cart, checkout, order review"
        noindex={true}
      />

      <div className="container-custom py-12">
        <h1 className="text-3xl font-display font-bold mb-8">{t('shoppingCart.title')}</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div key={item.id} className="card p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:gap-6">
                <div className="w-full sm:w-32 h-48 sm:h-32 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                  {item.product.image_url && (
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${item.product.slug}`}>
                    <h3 className="text-lg sm:text-xl font-bold hover:text-racing-red transition">
                      {item.product.name}
                    </h3>
                  </Link>
                  <p className="text-gray-600 text-sm mt-2">{item.product.short_description}</p>
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-4">
                    <span className="text-xl font-bold text-racing-red">
                      {item.product.category === 'handheld_tuners' 
                        ? formatPrice(1300, language) // Stage 1 price: 1300 PLN
                        : `$${item.product.price.toFixed(2)}`
                      }
                    </span>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">{t('shoppingCart.quantity')}</label>
                      <input
                        type="number"
                        min="1"
                        max={item.product.category === 'handheld_tuners' ? 999 : item.product.stock}
                        value={item.quantity}
                        onChange={(e) => updateCartItem(item.id, parseInt(e.target.value) || 1)}
                        className="w-16 px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-semibold sm:ml-auto"
                    >
                      {t('shoppingCart.remove')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-4">{t('shoppingCart.orderSummary')}</h2>
              <div className="space-y-2 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('shoppingCart.subtotal')}</span>
                  <span className="font-semibold">{formatPrice(cartTotal * 4, language)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('shoppingCart.shipping')}</span>
                  <span className="font-semibold">{t('shoppingCart.calculatedAtCheckout')}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-xl font-bold">
                    <span>{t('shoppingCart.total')}</span>
                    <span className="text-racing-red">{formatPrice(cartTotal * 4, language)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/checkout')}
                className="btn btn-primary w-full mb-4"
              >
                {t('shoppingCart.proceedToCheckout')}
              </button>
              <Link to="/shop" className="btn btn-outline w-full">
                {t('shoppingCart.continueShopping')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
